/* FAQ bento: animated, one-at-a-time accordion for
   `sections/faq-bento.liquid`.

   The markup already works without this file: native <details>/<summary>,
   and a shared `name` attribute makes the group exclusive. That native
   exclusivity closes the previous item instantly, so this script takes it
   over: it drops the `name` attributes, intercepts the summary click and
   animates the answer's height on the item being opened and on the one
   being closed. `open` stays the single source of truth, so keyboard,
   screen readers and find-in-page (which opens a <details> by itself) keep
   the native behaviour.

   One AbortController per section root (keyed by section id) lets
   `shopify:section:unload` remove exactly that instance's listeners; the
   element itself is the idempotency guard, because the editor re-renders a
   section into a fresh root.

   The same motion serves every other bento dropdown (owner, 2026-09-24):
   `data-m-faq="multi"` marks a group whose items open independently (the
   collection filter groups, the cart page's note and discount). Those take
   one delegated click listener on the document instead of per-item
   listeners, because their markup is replaced wholesale (the cart region's
   innerHTML swap) or moved (the filter form into the phone dialog). Pages
   load this file from several sections, so the file guards itself. */
(function () {
  if (window.LeonartoFaqBento) return;
  window.LeonartoFaqBento = true;

  var DURATION = 300;
  var EASING = 'ease-out';
  var controllers = {};
  var running = new WeakMap();

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function bodyOf(item) {
    return item.querySelector('[data-m-faq-body]');
  }

  function stop(item) {
    var animation = running.get(item);
    if (animation) {
      animation.cancel();
      running.delete(item);
    }
  }

  function animateHeight(item, from, to, done) {
    var body = bodyOf(item);
    if (!body || typeof body.animate !== 'function' || reducedMotion()) {
      done();
      return;
    }
    /* Clipped only while it moves: a body that stays `overflow: visible`
       keeps its focus rings (the filter checkboxes) when it is at rest. */
    body.style.overflow = 'hidden';
    var animation = body.animate([{ height: from + 'px' }, { height: to + 'px' }], {
      duration: DURATION,
      easing: EASING
    });
    running.set(item, animation);

    /* `finish` never fires while the document timeline is paused (a hidden
       tab or preview), which would leave a closing item open forever. The
       timer settles the state anyway; whichever comes first wins. */
    var settled = false;
    var timer;
    function settle() {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (running.get(item) === animation) running.delete(item);
      animation.cancel();
      body.style.overflow = '';
      done();
    }
    animation.onfinish = settle;
    animation.oncancel = function () {
      settled = true;
      clearTimeout(timer);
      if (!running.has(item)) body.style.overflow = '';
    };
    timer = setTimeout(settle, DURATION + 100);
  }

  /* `is-closing` marks an item whose `open` stays on until the slide ends:
     styles keyed to the open state (the plus/minus, the FAQ summary padding)
     read `[open]:not(.is-closing)` and so turn back as the slide starts,
     instead of snapping after it. */
  function close(item) {
    if (!item.open) return;
    var body = bodyOf(item);
    var from = body ? body.getBoundingClientRect().height : 0;
    stop(item);
    item.classList.add('is-closing');
    animateHeight(item, from, 0, function () {
      item.open = false;
      item.classList.remove('is-closing');
    });
  }

  function open(item) {
    var list = item.closest('[data-m-faq]');
    if (list && list.getAttribute('data-m-faq') !== 'multi') {
      Array.prototype.forEach.call(list.querySelectorAll('[data-m-faq-item]'), function (other) {
        if (other !== item) close(other);
      });
    }
    if (item.open && !running.has(item)) return;
    stop(item);
    item.classList.remove('is-closing');
    item.open = true;
    var body = bodyOf(item);
    animateHeight(item, 0, body ? body.scrollHeight : 0, function () {});
  }

  function sectionIdFor(list) {
    var section = list.closest('[id^="m-section-"]');
    return section ? section.id.replace(/^m-section-/, '') : null;
  }

  function init(list) {
    if (list.getAttribute('data-m-faq') === 'multi') return;
    if (list.dataset.mFaqInit === 'true') return;
    list.dataset.mFaqInit = 'true';

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var options = controller ? { signal: controller.signal } : false;

    Array.prototype.forEach.call(list.querySelectorAll('[data-m-faq-item]'), function (item) {
      item.removeAttribute('name');
      var summary = item.querySelector('summary');
      if (!summary) return;
      summary.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          /* A close still running counts as closed: clicking again reopens. */
          if (item.open && !running.has(item)) close(item);
          else open(item);
        },
        options
      );
    });

    /* Find-in-page and the editor can open an item without a click; keep
       the group exclusive when that happens. */
    list.addEventListener(
      'toggle',
      function (event) {
        var item = event.target;
        if (!item.open || !item.matches || !item.matches('[data-m-faq-item]')) return;
        Array.prototype.forEach.call(list.querySelectorAll('[data-m-faq-item]'), function (other) {
          if (other !== item && other.open && !running.has(other)) other.open = false;
        });
      },
      Object.assign({ capture: true }, controller ? { signal: controller.signal } : {})
    );

    /* A section can hold more than one list (the product page joins each run
       of accordion items into its own card), so one section id keeps a list
       of controllers. A re-render hands us fresh lists, and the unload below
       aborts every controller of that section. */
    var id = sectionIdFor(list);
    if (id && controller) {
      (controllers[id] = controllers[id] || []).push(controller);
    }
  }

  function initAll(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-m-faq]'), init);
  }

  /* Independent groups: one listener for all of them, wherever they are now. */
  document.addEventListener('click', function (event) {
    var summary = event.target.closest && event.target.closest('summary');
    if (!summary) return;
    var item = summary.parentElement;
    if (!item || !item.matches('[data-m-faq="multi"] [data-m-faq-item]')) return;
    event.preventDefault();
    if (item.open && !running.has(item)) close(item);
    else open(item);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var id = event.detail && event.detail.sectionId;
    if (id && controllers[id]) {
      controllers[id].forEach(function (controller) {
        controller.abort();
      });
      delete controllers[id];
    }
  });

  /* Selecting a question in the editor opens it (and closes the others). */
  document.addEventListener('shopify:block:select', function (event) {
    var item = event.target && event.target.closest && event.target.closest('[data-m-faq-item]');
    if (item) open(item);
  });
})();
